import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article, 3) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.category, to: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '催眠性福生活',
    },
    inLanguage: 'zh-Hans',
    url: `https://www.cuimianxingfu.com/article/${article.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Article Header */}
            <header className="py-4 md:py-6 border-b border-background-200/70 mb-6 md:mb-8">
              <div className="flex items-center gap-2 text-xs text-foreground-400 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="bg-secondary-100 text-secondary-700 px-2.5 py-0.5 rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {article.category}
                </Link>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom mb-10 md:mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-8 pt-6 border-t border-background-200/70">
              <span className="text-xs text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="py-8 md:py-10 border-t border-background-200/70">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group block bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden cursor-pointer"
                    >
                      <div className="relative h-32 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-xs md:text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Back Navigation */}
            <div className="py-6">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回 {article.category}
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}